import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Label, Button } from "reactstrap";
import PersonIncomeModal from "../../../../Modal/PersonIncome/PersonIncomeModal";

import { FormField, CheckFormField, SelectFormField, CustomFormInput } from "../../../../../utils/FormFields";
import { showToast } from "../../../../../utils/Helper";
import Autosuggest from "../../../../AutosuggestCompliance";
import CircularProggress from "../../../../CircularProgress/CircularProgress";
import CustomDropdown from "../../../../CustomDropdown/CustomDropdown";
import { useEffect, useState } from "react";

function PhysicalPersonLegalInfo({
  person,
  onChange,
  personLinks,
  activitySectors,
  publicPositions,
  patrimonies,
  professions,
  autosuggests,
  onChangeAutosuggest,
  disabled,
  errors,
  companyLinks,
  services,
  manual,
  role,
  currencies,
  refresh
}) {

  const [secondaryActivity, setSecondaryActivity] = useState(false);
  const [newTLFormulaEnabled, setNewTLFormulaEnabled] = useState(false);
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [incomes, setIncomes] = useState(null);
  const [incomesLoading, setIncomesLoading] = useState(false);
  // Meses de antigüedad válidos para las fechas del modal de ingresos (parametrizable).
  const [docExpirationMonths, setDocExpirationMonths] = useState(24);

  useEffect(() => {
    if (services && services.getParameterValue) {
      services.getParameterValue({ ParameterName: "DOC_EXPIRATION_MONTHS" }, (r) => {
        const v = r && r.SdtParametersData && Number(r.SdtParametersData.ParameterNumericValue);
        if (v && !Number.isNaN(v)) setDocExpirationMonths(v);
      });
    }
  }, []);

  // Al abrir "Add income data" traemos los incomes ya cargados para prellenar
  // las sub-tabs de la modal; recién ahí la abrimos (con spinner mientras carga).
  const openIncomeModal = () => {
    if (services && services.fetchPersonIncomesData && person && person.PersonId) {
      setIncomesLoading(true);
      services.fetchPersonIncomesData({ PersonId: person.PersonId }, (data) => {
        setIncomesLoading(false);
        setIncomes(data || []);
        setIncomeModalOpen(true);
      });
    } else {
      setIncomes([]);
      setIncomeModalOpen(true);
    }
  };

  // Al confirmar, persistimos la colección completa (existentes + nuevas) con
  // SetPersonIncomesData. Si el result es 200: cerramos la modal y refrescamos
  // la persona (para ver los totales recalculados). El modal muestra el error si falla.
  const handleIncomeSubmit = (payload, done) => {
    if (services && services.setPersonIncomesData && person && person.PersonId) {
      services.setPersonIncomesData(
        { PersonId: person.PersonId, SDTPersonIncomesCollection: payload.SDTPersonIncomesCollection },
        (data, err) => {
          const result = data && data.result;
          const ok = !err && result && Number(result.Code) === 200;
          if (ok) {
            showToast("Income data saved successfully");
            setIncomeModalOpen(false);
            if (refresh) refresh();
          } else {
            showToast((result && result.Description) || (err ? String(err) : "Error saving income data"), true);
          }
          if (done) done(ok);
        }
      );
    } else {
      setIncomeModalOpen(false);
      if (done) done(true);
    }
  };
  const fetchSectorSuggestion = (name) =>
    new Promise((resolve, reject) => {
      services.fetchAutosuggestSector({ text: name }, (response) => {
        if (response && response.sectors) {
          const suggestions = response.sectors.map((c) => ({
            id: c.Id,
            name: `${c.Description} (${c.Code})`,
          }));
          resolve(suggestions);
        } else {
          reject();
        }
      });
    });

  const onChangeSector = (value) => onChangeAutosuggest("PersonActivitySector", value);

  useEffect(()=>{
    services.getParameterValue(( { ParameterName: 'SecondaryActivitySectorOn' } ),(r) => {
      if (r.SdtParametersData.ParameterNumericValue == 0) {
        setSecondaryActivity(false)
      } else {
        setSecondaryActivity(true)
      }

    })},[])

  useEffect(() => {
    services.getParameterValue({ ParameterName: "NewTLFormula" }, (r) => {
      if (r && r.SdtParametersData) {
        setNewTLFormulaEnabled(r.SdtParametersData.ParameterNumericValue == 1);
      }
    });
  }, []);

  const handleSelectSector = (id, value) => {
    onChange("PersonActivitySectorId", id, value);
    onChangeSector(value);
  };

  const configuredDecimalSeparator =
  typeof window !== "undefined" &&
  window.___APP_CONFIG___ &&
  window.___APP_CONFIG___.DECIMAL_SEPARATOR;

  const decimalSeparator = configuredDecimalSeparator === "." ? "." : ",";
  const thousandSeparator = decimalSeparator === "." ? "," : ".";

  // Neutral panel used to highlight the financial fields row when the new TL
  // formula is on. Same style used elsewhere in the system (e.g. KYC tab).
  const highlightStyle = {
    border: "1px solid #d6d6d6",
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
    padding: "16px",
    marginBottom: "16px",
  };


  const personPublicPositionId = person.PhysicalPersonPublicPositionId;
  const isPEP = parseInt(personPublicPositionId, 10) > 1;
  const personLinkPositionId = person.PersonLinkPublicPositionId;
  const isPEPRelated = parseInt(personLinkPositionId, 10) > 1;

  return (
    <div>
      {!person.PersonIsMinimal && (
        <div style={newTLFormulaEnabled ? highlightStyle : undefined}>
          <Row>
            <Col>
              <FormField
                label="Person Transactional Limit: "
                disabled={disabled || role !== 'Compliance' || !person.manualTransactionalLimit}
                onChange={onChange}
                value={person.PersonTransactionalLimit}
                name="PersonTransactionalLimit"
                type="number"
                dataTypeDecimalName="currency"
                thousandSeparator={thousandSeparator}
              />
            </Col>
            {newTLFormulaEnabled && (
              <Col>
                <FormField
                  label="Estimated Net Worth: "
                  disabled={true}
                  onChange={onChange}
                  value={person.PersonEstimatedNetworth}
                  name="PersonEstimatedNetworth"
                  type="number"
                  dataTypeDecimalName="currency"
                  thousandSeparator={thousandSeparator}
                />
              </Col>
            )}
            <Col>
              <FormField
                label="Anual Income: "
                disabled={disabled || newTLFormulaEnabled}
                onChange={onChange}
                value={person.PersonAnualIncome}
                name="PersonAnualIncome"
                error={errors.PersonAnualIncome}
                type="number"
                dataTypeDecimalName="currency"
                thousandSeparator={thousandSeparator}
              />
            </Col>
            <Col>
              <FormField
                label="Anual Extra Income: "
                disabled={disabled || newTLFormulaEnabled}
                onChange={onChange}
                value={person.PersonExtraIncomeVolume}
                name="PersonExtraIncomeVolume"
                type="number"
                dataTypeDecimalName="currency"
                thousandSeparator={thousandSeparator}
              />
            </Col>
            <Col>
              {patrimonies ? (
                <SelectFormField
                  label="Patrimony: "
                  disabled={disabled}
                  error={errors.PhysicalPersonPatrimonyId}
                  name="PhysicalPersonPatrimonyId"
                  onChange={onChange}
                  selectItems={patrimonies}
                  getSelectItemId={(i) => i.PatrimonyId}
                  getSelectItemName={(i) => i.PatrimonyDescription}
                  value={person.PhysicalPersonPatrimonyId}
                />
              ) : (
                <CircularProggress sm />
              )}
            </Col>
          </Row>
          {newTLFormulaEnabled && services && services.fetchPersonIncomesData && (
            <Button color="link" className="p-0" onClick={openIncomeModal} disabled={incomesLoading} title="Load incomes">
              <i className={`fa ${incomesLoading ? "fa-spinner fa-spin" : "fa-plus-circle"}`} />{" "}
              Add income data
            </Button>
          )}
        </div>
      )}

      <PersonIncomeModal
        isOpen={incomeModalOpen}
        toggle={() => setIncomeModalOpen(false)}
        personType="Physical"
        minMonths={docExpirationMonths}
        currencies={currencies}
        onSubmit={handleIncomeSubmit}
        initialData={incomes}
        onRecalculate={
          services && services.recalculatePersonIncomes
            ? (payload, cb) =>
                services.recalculatePersonIncomes(
                  { PersonId: person.PersonId, ...payload },
                  (data) => cb(data)
                )
            : undefined
        }
      />

      {!person.PersonIsMinimal && (
        <div>
          <Row>
            <Col>
              <SelectFormField
                label="Profession: "
                value={person.PersonProfessionId}
                name="PersonProfessionId"
                onChange={onChange}
                selectItems={
                  professions || [
                    {
                      ProfessionId: person.PersonProfessionId,
                      ProfessionName: person.PersonProfessionName,
                    },
                  ]
                }
                getSelectItemId={(profession) => profession.ProfessionId}
                getSelectItemName={(profession) => profession.ProfessionName}
                disabled={disabled}
                error={errors.PersonProfessionId}
              />
            </Col>
            <Col>
              <CustomDropdown
                value={person.PersonActivitySectorId}
                label="Activity Sector: "
                name="PersonActivitySectorId"
                onChange={onChange}
                items={activitySectors}
                getSelectedId={(activitySector) => activitySector.Id}
                getSelectedName={(activitySector) => `${activitySector.Code} - ${activitySector.Description}`}
                getFilterCondition={(activitySector) =>
                  activitySector.Description + " " + activitySector.Code
                }
                disabled={disabled || !manual}
                error={errors.PersonActivitySectorId}
                extendInfo={true}
              />
            </Col>{ secondaryActivity &&
            <Col>
              <CustomDropdown
                value={person.PersonSecondActivitySectorId}
                label="Secondary Activity Sector: "
                name="PersonSecondActivitySectorId"
                onChange={onChange}
                items={activitySectors}
                getSelectedId={(activitySector) => activitySector.Id}
                getSelectedName={(activitySector) => `${activitySector.Code} - ${activitySector.Description}`}
                getFilterCondition={(activitySector) =>
                  activitySector.Description + " " + activitySector.Code
                }
                disabled={disabled || !manual}
                extendInfo={true}
              />
            </Col>}
            {/* <Col>
              <SelectFormField
                label="Activity Sector: "
                value={person.PersonActivitySectorId}
                name="PersonActivitySectorId"
                onChange={onChange}
                selectItems={
                  activitySectors || [
                    {
                      Id: person.PersonActivitySectorId,
                      Description: person.PersonActivitySectorName,
                    },
                  ]
                }
                getSelectItemId={(activitySector) => activitySector.Id}
                getSelectItemName={(activitySector) =>
                  `${activitySector.Description} - ${activitySector.Code}`
                }
                disabled={disabled || !manual}
                error={errors.PersonActivitySectorId}
              />
            </Col> */}
            {/* <Col>
              <CustomFormInput label="Activity Sector: " error={errors.PersonActivitySectorId}>
                <Autosuggest
                  value={autosuggests.PersonActivitySector}
                  onChange={onChangeSector}
                  fecthSuggestions={fetchSectorSuggestion}
                  onSelect={handleSelectSector}
                  disabled={disabled || !manual}
                />
              </CustomFormInput>
            </Col> */}
          </Row>
        </div>
      )}
      <SelectFormField
        label="Public Position: "
        value={personPublicPositionId}
        name="PhysicalPersonPublicPositionId"
        onChange={onChange}
        selectItems={
          publicPositions || [
            {
              PublicPositionId: person.PhysicalPersonPublicPositionId,
              PublicPositionName: person.PhysicalPersonPublicPositionName,
            },
          ]
        }
        getSelectItemId={(publicPosition) => publicPosition.PublicPositionId}
        getSelectItemName={(publicPosition) => publicPosition.PublicPositionName}
        disabled={disabled || !manual}
        error={errors.PhysicalPersonPublicPositionId}
        description={isPEP ? "PEP" : undefined}
      />
      {!person.PersonIsMinimal && (
        <div>
          <Row>
            <Col>
              <SelectFormField
                label="Link Person: "
                value={person.PhysicalPersonLinkId}
                name="PhysicalPersonLinkId"
                onChange={onChange}
                selectItems={
                  personLinks || [
                    {
                      PersonLinkId: person.PhysicalPersonLinkId,
                      PersonLinkName: person.PhysicalPersonLinkName,
                    },
                  ]
                }
                getSelectItemId={(personLink) => personLink.PersonLinkId}
                getSelectItemName={(personLink) => personLink.PersonLinkName}
                disabled={disabled || !manual}
                error={errors.PhysicalPersonLinkId}
              />
            </Col>
            <Col>
              <SelectFormField
                label="Link Person Public Position: "
                value={personLinkPositionId}
                name="PersonLinkPublicPositionId"
                onChange={onChange}
                selectItems={
                  publicPositions || [
                    {
                      PublicPositionId: person.PersonLinkPublicPositionId,
                      PublicPositionName: person.PersonLinkPublicPositionName,
                    },
                  ]
                }
                getSelectItemId={(publicPosition) => publicPosition.PublicPositionId}
                getSelectItemName={(publicPosition) => publicPosition.PublicPositionName}
                disabled={disabled || !manual}
                error={errors.PersonLinkPublicPositionId}
                description={isPEPRelated ? "PEP Related" : undefined}
              />
            </Col>
          </Row>
        </div>
      )}
      {!person.PersonIsMinimal && (
        <div>
          <FormField
            label="Institution: "
            value={person.PhysicalPersonInstitution}
            name="PhysicalPersonInstitution"
            onChange={onChange}
            disabled={disabled || !manual}
            error={errors.PhysicalPersonInstitution}
          />
          <div>
            <Label
              style={{
                fontWeight: 500,
                fontSize: "15px",
                color: "black",
                paddingRight: "10px",
              }}
            >
              {"Web User: "}
            </Label>
            <input
              value={person.PhysicalPersonWebUser}
              type="checkbox"
              onChange={(e) => onChange("PhysicalPersonWebUser", e.target.checked)}
              disabled={disabled}
              checked={person.PhysicalPersonWebUser}
            />
          </div>

          {companyLinks ? (
            <SelectFormField
              label="Company Link: "
              value={person.PersonCompanyLinkId}
              name="PersonCompanyLinkId"
              onChange={onChange}
              selectItems={companyLinks}
              getSelectItemId={(i) => i.CompanyLinkId}
              getSelectItemName={(i) => i.CompanyLinkName}
              error={errors.PersonCompanyLinkId}
              disabled={disabled}
            />
          ) : (
            <CircularProggress sm />
          )}
          <Row>
            <Col>
              <FormField
                label="Work History Activities: "
                name="PhysicalPersonWorkHistoryActivities"
                error={errors.PhysicalPersonWorkHistoryActivities}
                onChange={onChange}
                disabled={disabled}
                value={person.PhysicalPersonWorkHistoryActivities}
              />
            </Col>
            <Col>
              <FormField
                label="Work History Years: "
                name="PhysicalPersonWorkHistoryYears"
                error={errors.PhysicalPersonWorkHistoryYears}
                onChange={onChange}
                disabled={disabled}
                value={person.PhysicalPersonWorkHistoryYears}
                type="number"
                decimalScale ={0}
                thousandSeparator = {false}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormField
                label="Work History Relevant Position: "
                name="PhysicalPersonWorkHistoryRelevantPosition"
                error={errors.PhysicalPersonWorkHistoryRelevantPosition}
                onChange={onChange}
                disabled={disabled}
                value={person.PhysicalPersonWorkHistoryRelevantPosition}
              />
            </Col>
            <Col>
              <FormField
                label="Work History Income: "
                name="PhysicalPersonWorkHistoryIncome"
                error={errors.PhysicalPersonWorkHistoryIncome}
                onChange={onChange}
                disabled={disabled}
                value={person.PhysicalPersonWorkHistoryIncome}
                type="number"
                dataTypeDecimalName="currency"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormField
                label="Observations: "
                name="PersonObservations"
                //error={errors.PersonObservations}
                onChange={onChange}
                disabled={disabled}
                value={person.PersonObservations}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}

PhysicalPersonLegalInfo.propTypes = {
  person: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  personLinks: PropTypes.array,
  publicPositions: PropTypes.array,
  professions: PropTypes.array,
  autosuggests: PropTypes.object.isRequired,
  onChangeAutosuggest: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  companyLinks: PropTypes.array,
  patrimonies: PropTypes.array,
  services: PropTypes.object.isRequired,
};

PhysicalPersonLegalInfo.defaultProps = {
  personLinks: undefined,
  publicPositions: undefined,
  companyLinks: undefined,
  patrimonies: undefined,
  professions: undefined,
};

export default PhysicalPersonLegalInfo;
